import { Call, ContentCopy, Edit } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import InfoBlock from '~/components/infoBlock'
import { TInfoButton } from '~/components/infoBlock/infoButton'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import { EAgentKeys } from '~/features/agents/models'
import { getFormattedPhoneNumber } from '~/features/agents/utils'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import { EPhoneStatus, ETextStatus } from '~/features/settings/models'
import { ECapability } from '~/features/user/models'
import useCapabilities from '~/hooks/useCapabilities'
import { EClientPath } from '~/models/api'
import { ECognitoGroups } from '~/models/auth'

type TAgentPhoneInfoProps = {
  isExpanded: boolean
  tier: ECognitoGroups
}

function AgentPhoneInfo({ isExpanded, tier }: TAgentPhoneInfoProps) {
  const navigate = useNavigate()
  const isPartner = useIsPartner()
  const { canEdit } = useCapabilities()
  const canEditAiAssistantConfig = canEdit(ECapability.aiAssistantConfig)
  const { selectedAgent: agent } = useGetAgentCentralized()

  const agentPhoneIntroRecording = agent?.[EAgentKeys.phoneIntroRecording]
  const agentPhoneNumber = agent?.[EAgentKeys.phoneNumber]
  const agentPhoneStatus = agent?.[EAgentKeys.phoneStatus]
  const agentTextStatus = agent?.[EAgentKeys.textStatus]

  const displayPhoneNumber =
    getFormattedPhoneNumber(agentPhoneNumber ?? '') || ''
  const isTextingActive = agentTextStatus === ETextStatus.active
  const showTextAction =
    isTextingActive && tier === ECognitoGroups.customerPlusPlan

  const isChannelActive =
    agentPhoneStatus === EPhoneStatus.active &&
    agentPhoneIntroRecording !== null
  const isFreePlan = tier === ECognitoGroups.customerFreePlan

  let message = displayPhoneNumber
  let showActiveActions = true

  if (!isFreePlan) {
    if (!agentPhoneNumber) {
      message = isChannelActive ? 'Set up your phone number.' : 'Activate phone'
      showActiveActions = false
    }

    if (agentPhoneNumber && !isChannelActive) {
      message = 'Activate phone'
      showActiveActions = false
    }
  }

  if (isFreePlan) {
    message = 'Upgrade to enable phone and text.'
    showActiveActions = false
  }

  const handleEditPhone = () => {
    if (!showActiveActions && isFreePlan) {
      navigate(isPartner ? EClientPath.account : EClientPath.billing)
      return
    }

    navigate(EClientPath.settingsPhone)
  }

  const handleCall = () => {
    window.location.href = `tel:${displayPhoneNumber}`
  }

  const handleText = () => {
    window.location.href = `sms:${agentPhoneNumber}`
  }

  const textAction: TInfoButton[] = showTextAction
    ? [{ label: 'Text', variant: 'contained', onClick: handleText }]
    : []

  const activeActions: TInfoButton[] = (
    [
      {
        icon: <ContentCopy />,
        label: 'Copy',
        variant: 'outlined',
        textToCopy: displayPhoneNumber,
      },
      ...(canEditAiAssistantConfig
        ? [
            {
              icon: <Edit />,
              label: 'Edit',
              variant: 'outlined' as const,
              onClick: handleEditPhone,
            },
          ]
        : []),
      {
        label: 'Call',
        variant: 'contained' as const,
        onClick: handleCall,
      },
    ] as TInfoButton[]
  ).concat(textAction)

  const disabledActions: TInfoButton[] = canEditAiAssistantConfig
    ? [
        {
          icon: <Edit />,
          label: 'Edit',
          variant: 'outlined',
          onClick: handleEditPhone,
        },
      ]
    : []

  const actions = showActiveActions ? activeActions : disabledActions

  let onLabelClick: (() => void) | undefined
  if (showActiveActions) {
    onLabelClick = handleCall
  } else if (isFreePlan || canEditAiAssistantConfig) {
    onLabelClick = handleEditPhone
  }

  return (
    <InfoBlock
      icon={<Call />}
      infoText={
        <>
          <p>How to use your AI assistant phone number</p>
          <ul style={{ margin: 0, paddingLeft: 16, paddingBottom: 16 }}>
            <li>
              First customize your phone settings, customizing the phone number,
              intro message, voice, and more
            </li>
            <li>Forward your existing business number to your assistant</li>
            <li>
              Update email signatures, Google Business Profile and printed
              materials with your AI assistant number
            </li>
            <li>
              If texting is set up, tell visitors to &quot;Call or Text us&quot;
              on your website and signage
            </li>
          </ul>
        </>
      }
      title="Phone"
      value={message}
      isExpanded={isExpanded}
      actionButtons={actions}
      onLabelClick={onLabelClick}
    />
  )
}

export default AgentPhoneInfo
